/**
 * TypeScript types for the Connections Scorecard dashboard.
 * Mirror GraphQL schema in DasFinSuiteBackendCDK.
 */

export type RangeIndicator = 'above' | 'within' | 'below' | null;
export type MissingValueReason = 'NO_MEASUREMENTS' | 'INSUFFICIENT_MEASUREMENTS' | null;
export type Sentiment = 'POSITIVE' | 'NEGATIVE';

export const CONNECTIONS_THEMES = ['CAREER_GROWTH', 'CULTURE', 'INCLUSION', 'JOB_SATISFACTION'] as const;
export type Theme = (typeof CONNECTIONS_THEMES)[number];

export const THEME_DISPLAY_NAMES: Record<Theme, string> = {
  CAREER_GROWTH: 'Career Growth',
  CULTURE: 'Culture',
  INCLUSION: 'Inclusion',
  JOB_SATISFACTION: 'Job Satisfaction'
};

export const CONNECTIONS_SCOPES = [
  { value: 'COMPASS_CORP', label: 'Compass Corp (default)' },
  { value: 'COMPASS_TECH', label: 'Compass Tech' },
  { value: 'COMPASS_NON_TECH', label: 'Compass Non-Tech' },
  { value: 'Census', label: 'Census' },
  { value: 'LL WW All', label: 'Lower-level Worldwide' }
] as const;

export const CONNECTIONS_SUMMARY_TYPES = [
  { value: 'ADJUSTED_POSITIVE_RESPONSE_RATE', label: 'Adjusted Positive Response Rate' },
  { value: 'ADJUSTED_NEGATIVE_RESPONSE_RATE', label: 'Adjusted Negative Response Rate' },
  { value: 'RESPONSE_RATE', label: 'Response Rate' },
  { value: 'MEAN_LIKERT_SCORE', label: 'Mean Likert Score' }
] as const;

export interface ConnectionsLeader {
  leaderId: string;
  leaderLogin: string;
  displayName: string;
  orgLogin: string | null;
  level: string | null;
  span: string | null;
  hasDirectOrgBreakdown: boolean;
  isActive: boolean;
}

export interface ConnectionsThemeScore {
  theme: string;
  metricId: string | null;
  displayName: string | null;
  metricValue: number | null;
  unadjustedMetricValue?: number | null;
  sampleSize: number | null;
  populationSize: number | null;
  uniqueRespondents?: number | null;
  benchmarkMean: number | null;
  benchmarkMin: number | null;
  benchmarkMax: number | null;
  rangeIndicator: RangeIndicator;
  missingValueReason: MissingValueReason;
}

export interface ConnectionsScorecard {
  leaderLogin: string;
  leaderId: string;
  displayName: string | null;
  metricPeriod: string;
  scope: string | null;
  summaryType: string | null;
  groupByDimension: string | null;
  groupByValue?: string | null;
  hasDirectOrgBreakdown: boolean | null;
  lastUpdated: string | null;
  themes: ConnectionsThemeScore[];
}

export interface ConnectionsTrendPoint {
  startDate: string;
  metricPeriod: string;
  metricValue: number | null;
  sampleSize: number | null;
  benchmarkMean: number | null;
  benchmarkMin: number | null;
  benchmarkMax: number | null;
  rangeIndicator: RangeIndicator;
  missingValueReason: MissingValueReason;
}

export interface ConnectionsFreetextTopic {
  topicId: string;
  topicName: string;
  sentiment: Sentiment;
  metricValue: number | null;
  responseCount: number | null;
  sampleSize: number | null;
}

export interface ConnectionsQuestionResponse {
  questionId: string;
  questionText: string | null;
  parentQuestionId: string | null;
  parentQuestionText: string | null;
  responseOptionId: string | null;
  responseOptionText: string | null;
  score: number | null;
  responseCount: number | null;
  sampleSize: number | null;
}

/**
 * Raw verbatim free-text response (from the monthly Clarity static file).
 * One row per verbatim; powers the word cloud + drill-down. sentiment may be
 * 'Unknown' for the open-ended "Other Free Text Responses" prompts that are not
 * positive/negative classified. writtenResponse is HR-PII (access-scoped server-side).
 */
export type ResponseSentiment = 'POSITIVE' | 'NEGATIVE' | 'Unknown';

export interface ConnectionsFreetextResponse {
  responseHash: string;
  questionText: string | null;
  sentiment: ResponseSentiment | null;
  topic: string | null;
  writtenResponse: string;
  theme: string | null;
  dataMonth: string | null;
}

/**
 * A single headcount/attrition data point (long/tidy) from getConnectionsHeadcount.
 * One row per leader x metricName x metricMonth. metricName is the readable
 * description (e.g. "Active Workforce (Beginning)", "External Terminations",
 * "Attrition Pct - Annualized"); metricValue is a count or a percentage
 * depending on the metric. scenario is always 'Actuals'.
 */
export interface ConnectionsHeadcountPoint {
  leaderLogin: string;
  leaderEmployeeId: string | null;
  displayName: string | null;
  scenario: string | null;
  metricName: string;
  metricMonth: string;
  metricValue: number | null;
}
